import React from 'react'
import cn from 'classnames'
import { X } from 'lucide-react'
import { RESERVED_AUDIO_CARD_HEIGHT } from 'lib/mediaBuckets'
import AudioAttachmentPlayer from './AudioAttachmentPlayer'
import AudioArtworkCard, { AudioArtworkTitle } from './AudioArtworkCard'
import { mediaFileTitle } from './ordinalMediaTitle'
import styles from './ordinal-audio-card.module.scss'

type AudioAttachmentCardProps = React.ComponentProps<typeof AudioAttachmentPlayer> & {
    /** Image+audio hosts already supply artwork and only need the caption and transport. */
    artwork?: boolean
}

export default function AudioAttachmentCard({ artwork, ...playerProps }: AudioAttachmentCardProps) {
    const showArtwork = artwork ?? playerProps.theme !== 'scrim'
    const title = mediaFileTitle(playerProps.fileName) || playerProps.trackTitle || 'Audio'
    const player = (
        <AudioAttachmentPlayer
            {...playerProps}
            trackTitle={playerProps.trackTitle || title}
            theme={showArtwork ? 'scrim' : playerProps.theme}
            compact={showArtwork ? false : playerProps.compact}
            volumeFlyout={showArtwork || playerProps.volumeFlyout}
            className={cn(playerProps.className, styles.transport)}
            matchingBsvAttachment={showArtwork ? undefined : playerProps.matchingBsvAttachment}
            showDelete={showArtwork ? false : playerProps.showDelete}
        />
    )

    if (!showArtwork) {
        return (
            <>
                <AudioArtworkTitle title={title} />
                {player}
            </>
        )
    }

    const hasTopControls = playerProps.matchingBsvAttachment || playerProps.showDelete

    return (
        <AudioArtworkCard
            title={title}
            previewUrl={playerProps.posterSrc}
            // Preserve the renderer/estimator contract for standalone and two-up audio rows.
            style={{ height: RESERVED_AUDIO_CARD_HEIGHT }}
            topControls={
                hasTopControls && (
                    <div className={styles.attachmentControls}>
                        {playerProps.matchingBsvAttachment &&
                            playerProps.renderBsvAttachmentStatus?.(
                                playerProps.matchingBsvAttachment,
                            )}
                        {playerProps.showDelete && (
                            <button
                                type="button"
                                className={styles.removeAttachment}
                                aria-label="Remove audio attachment"
                                onClick={playerProps.onDelete}
                            >
                                <X size={16} aria-hidden="true" />
                            </button>
                        )}
                    </div>
                )
            }
        >
            {player}
        </AudioArtworkCard>
    )
}
